import "@/components/Layout/Table/TableSortButton/TableSortButton.scss";

import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { FCC } from "types";

import { SortDown, SortUp } from "@/assets/icons/ds";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

interface Props {
  title: string;
  sortField: string;
}

const ariaSortMapping = { asc: "ascending", desc: "descending" } as const;

/**
 * A sortable column header: the <th> carries aria-sort for the column's
 * current sort state (read from the same URL params TableSortButton writes).
 */
export const SortableHeaderCell = ({
  title,
  sortField,
  className,
  dataTestid,
}: Props & { className?: string; dataTestid?: string }) => {
  const [searchParams] = useSearchParams();
  const direction = searchParams.get("sortDirection");
  const isActive = searchParams.get("sortField") === sortField;
  const ariaSort =
    isActive && (direction === "asc" || direction === "desc")
      ? ariaSortMapping[direction]
      : "none";

  return (
    <th data-testid={dataTestid} className={className} aria-sort={ariaSort}>
      <TableSortButton title={title} sortField={sortField} />
    </th>
  );
};

export const TableSortButton: FCC<Props> = ({ title, sortField }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentlyActiveSortField = searchParams.get("sortField");
  const sortFieldIsActive = currentlyActiveSortField === sortField;
  const currentlyActiveSortDirection = searchParams.get("sortDirection");

  const setSortField = (sortField: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());

    // Re-sorting reorders the whole list, so the old page number points at an
    // arbitrary mid-list slice of the new order. Back to page 1 — dropping the
    // param is how the tables express that (`clampPage` reads a missing `page`
    // as 1).
    updatedSearchParams.delete("page");

    if (sortFieldIsActive) {
      if (currentlyActiveSortDirection === "asc") {
        updatedSearchParams.set("sortDirection", "desc");
      }
      if (currentlyActiveSortDirection === "desc") {
        updatedSearchParams.delete("sortField");
        updatedSearchParams.delete("sortDirection");
      }
    } else {
      updatedSearchParams.set("sortField", sortField);
      updatedSearchParams.set("sortDirection", "asc");
    }
    setSearchParams(updatedSearchParams.toString());
  };

  return (
    <button
      className="sortable group relative outline-none"
      type="button"
      tabIndex={KEYBOARD_TAB_INDEX}
      aria-label={t("layout.table.sortBy", { title })}
      title={t("layout.table.sortBy", { title })}
      onClick={() => setSortField(sortField)}
    >
      <FocusRing />
      {/* min-w-0 lets a long single-word header break instead of overflowing its
          column. Where the break should land — and whether it shows a hyphen —
          is not something CSS can express for a compound noun, so the German
          German "Invoice number" (one compound word) carries a soft hyphen
          (`U+00AD`) at the compound boundary (see locales/de): invisible while
          the word fits on one line, rendered as "-" only where the browser
          actually breaks it. Otherwise the column width alone decides and it
          splits mid-syllable with no hyphen at all. */}
      <span className="min-w-0 break-words">{title}</span>
      {/* No glyph while the column is unsorted. */}
      {sortFieldIsActive &&
        (currentlyActiveSortDirection === "desc" ? (
          <SortDown data-testid="sort-glyph-desc" className="arrow" />
        ) : (
          <SortUp data-testid="sort-glyph-asc" className="arrow" />
        ))}
    </button>
  );
};
